import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, advisorsAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import '../styles/adminCommonStyles.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB - must match backend limit

export default function AdminAdvisors() {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const refetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await advisorsAPI.getAdvisors();
      const { normalizeList } = await import('../utils/responseHelpers');
      setAdvisors(normalizeList(res));
    } catch (err) {
      setFetchError(err.response?.data?.message || err.message || 'Failed to fetch advisors');
    } finally {
      setLoading(false);
    }
  }, []);
  // Fetch areas of focus from backend
  const fetchAreas = useCallback(async () => {
    setAreasLoading(true);
    setAreasError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(API_BASE + '/api/areas-of-focus', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch areas of focus');
      const data = await res.json();
      const areasList = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
      // Store full objects {id, name} for deletion functionality
      setAreasOfFocus(areasList);
      // Return just names for backwards compatibility
      const names = areasList.map(a => typeof a === 'string' ? a : a.name);
      return names;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch areas of focus';
      setAreasError(typeof errorMsg === 'string' ? errorMsg : 'Failed to fetch areas of focus');
      console.error('Areas fetch error:', err);
      return [];
    } finally {
      setAreasLoading(false);
    }
  }, []);

  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', name: '', area_of_focus: '', bio: '', is_top_advisor: false });
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', area_of_focus: '', bio: '', is_top_advisor: false });
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addImage, setAddImage] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [removeEditPhoto, setRemoveEditPhoto] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Areas of focus state
  const [areasOfFocus, setAreasOfFocus] = useState([]); // Array of {id, name} objects
  const [areasLoading, setAreasLoading] = useState(true);
  const [areasError, setAreasError] = useState(null);
  const [newAreaOfFocus, setNewAreaOfFocus] = useState('');
  const [showAddArea, setShowAddArea] = useState(false);
  const [addAreaError, setAddAreaError] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Cleanup unused areas of focus
  const handleCleanupAreas = useCallback(async () => {
    if (!window.confirm('This will delete all areas of focus that are not assigned to any advisor. Are you sure?')) {
      return;
    }
    
    setCleanupLoading(true);
    setCleanupResult(null);
    
    try {
      const token = localStorage.getItem('token');
      console.log('[Cleanup] Starting cleanup of unused areas...');
      
      const res = await fetch(API_BASE + '/api/areas-of-focus/cleanup', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      console.log('[Cleanup] Response:', data);
      
      if (!res.ok) {
        throw new Error(data.message || 'Cleanup failed');
      }
      
      if (data.deleted_count > 0) {
        console.log('[Cleanup] Deleted ' + data.deleted_count + ' areas:', data.deleted_areas);
        setCleanupResult({
          success: true,
          message: `Successfully deleted ${data.deleted_count} unused area${data.deleted_count !== 1 ? 's' : ''}: ${data.deleted_areas.join(', ')}`,
          deletedCount: data.deleted_count
        });
        
        // Refresh areas list
        await fetchAreas();
        
        // Show success message
        showSuccess(`Cleanup complete! Deleted ${data.deleted_count} unused area${data.deleted_count !== 1 ? 's' : ''}.`);
      } else {
        setCleanupResult({
          success: true,
          message: 'No unused areas found - all areas are being used!',
          deletedCount: 0
        });
      }
    } catch (err) {
      console.error('[Cleanup] Error:', err);
      setCleanupResult({
        success: false,
        message: err.message || 'Cleanup failed'
      });
    } finally {
      setCleanupLoading(false);
    }
  }, [fetchAreas]);
  const navigate = useNavigate();
  const addModalRef = useRef(null);
  const editModalRef = useRef(null);

  useEffect(() => {
    setIsSuperAdmin(authAPI.hasRole('superadmin'));
    setIsAdmin(authAPI.hasRole('admin'));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  // Scroll to top when modals open - only if form exceeds viewport height
  useEffect(() => {
    // When add modal opens, ensure viewport is at top so the form starts from page top
    if (showAddModal) {
      scrollToForm();
    }
  }, [showAddModal]);

  useEffect(() => {
    // When edit modal opens, ensure viewport is at top so the form starts from page top
    if (showEditModal) {
      scrollToForm();
    }
  }, [showEditModal]);

  // Success message helper
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Add advisor handlers
  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };
  const handleAddImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setAddError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setAddError(null);
    setAddImage(file);
  };
  const handleAddArea = async (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    const trimmed = newAreaOfFocus.trim();
    if (!trimmed) {
      setAddAreaError('Please fill in the area name first');
      console.warn('[AddArea] Validation Error: Empty area name submitted');
      return;
    }
    // Check if area already exists (comparing names in object array)
    const existingArea = areasOfFocus.find(a => a.name === trimmed);
    if (existingArea) {
      // Area already exists — just select it
      if (showAddModal) setAddForm(f => ({ ...f, area_of_focus: trimmed }));
      if (showEditModal) setEditForm(f => ({ ...f, area_of_focus: trimmed }));
      setShowAddArea(false);
      setNewAreaOfFocus('');
      return;
    }
    setAddAreaError('');
    try {
      const token = localStorage.getItem('token');
      console.log('[AddArea] Token:', token ? 'Present' : 'Missing');
      console.log('[AddArea] API_BASE:', API_BASE);
      console.log('[AddArea] Full URL:', API_BASE + '/api/areas-of-focus');
      console.log('[AddArea] Request body:', { name: trimmed });
      
      const res = await fetch(API_BASE + '/api/areas-of-focus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: trimmed })
      });
      let apiResult = null;
      try { apiResult = await res.json(); } catch (jsonErr) { console.error('[AddArea] JSON parse error:', jsonErr); }
      console.log('[AddArea] POST response status:', res.status, 'body:', apiResult);
      if (!res.ok) {
        const errorMsg = (apiResult && (apiResult.error || apiResult.message)) || `HTTP ${res.status}: Failed to add area of focus`;
        console.error('[AddArea] Error response:', {
          status: res.status,
          message: errorMsg,
          apiResult: apiResult,
          areaName: trimmed,
          timestamp: new Date().toISOString()
        });
        throw new Error(errorMsg);
      }
      // Optimistically add the new area as an object {id, name}
      const newAreaObj = { id: apiResult?.id || null, name: trimmed };
      setAreasOfFocus(prev => prev.some(a => a.name === trimmed) ? prev : [...prev, newAreaObj]);
      // Select the new area in the active form
      if (showAddModal) setAddForm(f => ({ ...f, area_of_focus: trimmed }));
      if (showEditModal) setEditForm(f => ({ ...f, area_of_focus: trimmed }));
      // Don't refetch - the area will be persisted when an advisor is created/updated with it
      setShowAddArea(false);
      setNewAreaOfFocus('');
      console.log('[AddArea] Success, area added:', newAreaObj);
    } catch (err) {
      const errorMsg = err.message || 'Failed to add area of focus';
      console.error('[AddArea] Exception - Full Error Details:', {
        message: err.message,
        stack: err.stack,
        areaName: trimmed,
        timestamp: new Date().toISOString(),
        originalError: err
      });
      setAddAreaError(errorMsg); // show inline — do NOT close the input on error
    }
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
      // If image present, use FormData
      let response;
      if (addImage) {
        const form = new FormData();
        form.append('name', addForm.name);
        form.append('area_of_focus', addForm.area_of_focus);
        form.append('bio', addForm.bio);
        form.append('is_top_advisor', addForm.is_top_advisor ? 1 : 0);
        form.append('photo', addImage);
        response = await advisorsAPI.createAdvisor(form, true);
      } else {
        const data = { name: addForm.name, area_of_focus: addForm.area_of_focus, bio: addForm.bio, is_top_advisor: addForm.is_top_advisor ? 1 : 0 };
        response = await advisorsAPI.createAdvisor(data);
      }
      if (response.data?.success) {
        setShowAddModal(false);
        setAddForm({ name: '', area_of_focus: '', bio: '', is_top_advisor: false });
        setAddImage(null);
        showSuccess('Advisor added successfully!');
        // Refresh advisors list after successful creation
        await refetch();
      } else {
        setAddError(response.data?.message || 'Failed to add advisor');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add advisor';
      setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add advisor');
    } finally {
      setAddLoading(false);
    }
  };

  // Edit advisor handlers
  const handleEditClick = (advisor) => {
    setEditForm({
      id: advisor.id,
      name: advisor.name,
      area_of_focus: advisor.area_of_focus,
      bio: advisor.bio,
      is_top_advisor: advisor.is_top_advisor ? true : false,
      photo_url: advisor.photo_url || '',
      _photoSrc: advisor._photoSrc || null
    });
    setEditImage(null);
    setRemoveEditPhoto(false);
    setShowEditModal(true);
    setEditError(null);
    // Ensure viewport at top so edit form appears from page top
    scrollToForm();
  };
  const handleEditImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setEditError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setEditError(null);
    setEditImage(file);
  };
  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      let response;
      if (editImage) {
        const form = new FormData();
        form.append('name', editForm.name);
        form.append('area_of_focus', editForm.area_of_focus);
        form.append('bio', editForm.bio);
        form.append('is_top_advisor', editForm.is_top_advisor ? 1 : 0);
        form.append('photo', editImage);
        form.append('removePhoto', 'false');
        response = await advisorsAPI.updateAdvisor(editForm.id, form, true);
      } else if (removeEditPhoto) {
        // Removing photo doesn't require multipart upload — send JSON so backend
        // getJsonBody() properly receives the `removePhoto` flag on PUT.
        const data = {
          name: editForm.name,
          area_of_focus: editForm.area_of_focus,
          bio: editForm.bio,
          is_top_advisor: editForm.is_top_advisor ? 1 : 0,
          removePhoto: true
        };
        response = await advisorsAPI.updateAdvisor(editForm.id, data);
      } else {
        const data = { name: editForm.name, area_of_focus: editForm.area_of_focus, bio: editForm.bio, is_top_advisor: editForm.is_top_advisor ? 1 : 0 };
        response = await advisorsAPI.updateAdvisor(editForm.id, data);
      }
      if (response.data?.success) {
        setShowEditModal(false);
        setEditImage(null);
        setRemoveEditPhoto(false);
        showSuccess('Advisor updated successfully!');
        // Refresh advisors list after successful edit
        await refetch();
      } else {
        setEditError(response.data?.message || 'Failed to update advisor');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update advisor';
      setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update advisor');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete advisor
  const handleDeleteAdvisor = async (advisorId, advisorName) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete advisors.');
      return;
    }
    const confirmDelete = window.confirm(`Are you sure you want to delete "${advisorName}"? This action cannot be undone.`);
    if (!confirmDelete) return;
    setDeleteLoading(advisorId);
    setError(null);
    try {
      await advisorsAPI.deleteAdvisor(advisorId);
      // Refresh advisors list after delete
      await refetch();
      showSuccess('Advisor deleted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete advisor';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to delete advisor');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Logout
  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  // Permission logic
  const canEdit = isSuperAdmin || isAdmin;
  const canAdd = isSuperAdmin;
  const canDelete = isSuperAdmin;
  // ...existing code...
  return (
    <div className="w-full max-w-screen-2xl mx-auto px-1 md:px-4 py-4 mt-2 landscape:px-8 landscape:py-8 flex flex-col items-center justify-center">
      <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6 md:gap-0">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left drop-shadow-lg">Advisors</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>{isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}</span>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border border-red-300"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="admin-alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {canAdd && (
        <div className="w-full flex flex-col sm:flex-row gap-2 justify-center sm:justify-start mb-6">
          <button
            className="admin-btn-primary-lg"
            onClick={() => {
              setShowAddModal(true);
              // Ensure viewport at top so add form appears from page top
              scrollToForm();
            }}
          >
            + Add New Advisor
          </button>
          <button
            className="admin-btn-secondary px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-all"
            onClick={handleCleanupAreas}
            disabled={cleanupLoading}
            title="Remove unused areas of focus from the database"
          >
            {cleanupLoading ? 'Cleaning...' : '🧹 Cleanup Unused Areas of focus'}
          </button>
        </div>
      )}

      {cleanupResult && (
        <div className={`w-full mb-4 p-3 rounded-lg ${cleanupResult.success ? 'bg-green-50 border border-green-300' : 'bg-red-50 border border-red-300'}`}>
          <p className={cleanupResult.success ? 'text-green-800' : 'text-red-800'}>
            {cleanupResult.message}
          </p>
        </div>
      )}

      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content" ref={addModalRef}>
            <h3 className="admin-modal-title">Add New Advisor</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Area of Focus</label>
                <div className="flex gap-2 items-center">
                  <select
                    name="area_of_focus"
                    value={addForm.area_of_focus}
                    onChange={handleAddInput}
                    className="admin-input-special"
                    required
                  >
                    <option value="" disabled>Select area of focus</option>
                    {areasLoading ? (
                      <option>Loading...</option>
                    ) : areasOfFocus.length === 0 ? (
                      <option disabled>No areas found</option>
                    ) : (
                      areasOfFocus.map((area, idx) => (
                        <option key={idx} value={area.name}>{area.name}</option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    onClick={() => setShowAddArea(true)}
                  >
                    + Add Area
                  </button>
                </div>
              </div>
              {showAddArea && (
                <div className="flex flex-col gap-2 mt-2 mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAreaOfFocus}
                      onChange={e => { setNewAreaOfFocus(e.target.value); setAddAreaError(''); }}
                      className="flex-1 border border-gray-300 px-2 py-1 rounded"
                      placeholder="New area of focus"
                    />
                    <button
                      type="button"
                      className="admin-btn-primary"
                      onClick={handleAddArea}
                    >Add</button>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => { setShowAddArea(false); setNewAreaOfFocus(''); setAddAreaError(''); }}
                    >Cancel</button>
                  </div>
                  {addAreaError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                      {addAreaError}
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Bio</label>
                <Editor
                  value={addForm.bio}
                  onEditorChange={val => setAddForm(f => ({ ...f, bio: val }))}
                  init={{
                    apiKey: process.env.REACT_APP_TINYMCE_API_KEY,
                    height: 240,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Is Top Advisor</label>
                <select
                  name="is_top_advisor"
                  value={addForm.is_top_advisor ? 'true' : 'false'}
                  onChange={(e) => setAddForm({ ...addForm, is_top_advisor: e.target.value === 'true' })}
                  className="admin-input"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="admin-label">Photo</label>
                <input type="file" name="photo" accept="image/*" onChange={(e) => setAddImage(e.target.files[0])} className="admin-input" />
                <div className="text-xs text-gray-500 mt-1">Optional: upload advisor photo (JPEG, PNG, WebP)</div>
              </div>
              {addError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {addError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Advisor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="admin-modal-overlay-scroll">
          <div className="admin-modal-content" ref={editModalRef}>
            <h3 className="text-xl font-bold mb-4">Edit Advisor</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Area of Focus</label>
                <div className="flex gap-2 items-center">
                  <select
                    name="area_of_focus"
                    value={editForm.area_of_focus}
                    onChange={handleEditInput}
                    className="admin-input-special"
                    required
                  >
                    <option value="" disabled>Select area of focus</option>
                    {areasLoading ? (
                      <option>Loading...</option>
                    ) : areasOfFocus.length === 0 ? (
                      <option disabled>No areas found</option>
                    ) : (
                      areasOfFocus.map((area, idx) => (
                        <option key={idx} value={area.name}>{area.name}</option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    onClick={() => setShowAddArea(true)}
                  >
                    + Add Area
                  </button>
                </div>
                {showAddArea && (
                  <div className="flex flex-col gap-2 mt-2 mb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newAreaOfFocus}
                        onChange={e => { setNewAreaOfFocus(e.target.value); setAddAreaError(''); }}
                        className="flex-1 border border-gray-300 px-2 py-1 rounded"
                        placeholder="New area of focus"
                      />
                      <button
                        type="button"
                        className="admin-btn-primary"
                        onClick={handleAddArea}
                      >Add</button>
                      <button
                        type="button"
                        className="admin-btn-secondary"
                        onClick={() => { setShowAddArea(false); setNewAreaOfFocus(''); setAddAreaError(''); }}
                      >Cancel</button>
                    </div>
                    {addAreaError && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                        {addAreaError}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="admin-label">Bio</label>
                <Editor
                  value={editForm.bio}
                  onEditorChange={val => setEditForm(f => ({ ...f, bio: val }))}
                  init={{
                    apiKey: process.env.REACT_APP_TINYMCE_API_KEY,
                    height: 240,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Is Top Advisor</label>
                <select
                  name="is_top_advisor"
                  value={editForm.is_top_advisor ? 'true' : 'false'}
                  onChange={(e) => setEditForm({ ...editForm, is_top_advisor: e.target.value === 'true' })}
                  className="admin-input"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Photo</label>
                {editForm.photo_url && !removeEditPhoto && (
                  <div className="mb-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 mb-2">Current Photo:</p>
                    <img
                      src={editForm._photoSrc ? editForm._photoSrc : (editForm.photo_url.startsWith('/') ? API_BASE + editForm.photo_url + `?t=${Date.now()}` : editForm.photo_url)}
                      alt="Current"
                      className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      className="mt-3 admin-btn-danger"
                      onClick={() => setRemoveEditPhoto(true)}
                      disabled={editLoading}
                    >
                      Remove Photo
                    </button>
                  </div>
                )}
                {removeEditPhoto && (
                  <div className="mb-3 p-4 bg-red-50 border border-red-300 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mb-2">Photo will be removed</p>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setRemoveEditPhoto(false)}
                      disabled={editLoading}
                    >
                      Keep Photo
                    </button>
                  </div>
                )}
                {!removeEditPhoto && (
                  <>
                    <input type="file" name="photo" accept="image/*" onChange={handleEditImage} className="admin-input" />
                    {editImage && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                        <p className="text-sm font-medium text-[#3a8b9e] mb-2">New Photo Preview:</p>
                        <img src={URL.createObjectURL(editImage)} alt="New" className="h-32 w-32 object-cover rounded-lg border border-blue-300" />
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">Leave empty to keep the current photo</div>
                  </>
                )}
              </div>
              {editError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Advisor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading advisors..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
  <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Photo</th>
                <th className="admin-table-header-cell">Name</th>
                <th className="admin-table-header-cell">Area of Focus</th>
                <th className="admin-table-header-cell">Bio</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {advisors.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No advisors found</div>
                    <div className="text-sm mt-1">Add your first advisor to get started!</div>
                  </td>
                </tr>
              ) : (
                advisors.map((advisor) => (
                  <tr key={advisor.id} className="hover:bg-blue-50 transition-colors">
                    <td className="py-4 px-4">
                      {advisor._photoSrc ? (
                        <img src={advisor._photoSrc} alt={advisor.name} className="h-12 w-12 object-cover rounded border" />
                      ) : advisor.photo_url ? (
                        <img src={(process.env.REACT_APP_API_BASE_URL || '/backend/php') + advisor.photo_url + `?t=${Date.now()}`} alt={advisor.name} className="h-12 w-12 object-cover rounded border" />
                      ) : (
                        <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">No photo</div>
                      )}
                    </td>
                    <td className="py-4 px-4 font-medium text-gray-900">{advisor.name && advisor.name.length > 50 ? advisor.name.slice(0, 50) + '…' : advisor.name}</td>
                    <td className="py-4 px-4 font-medium text-gray-900">
                      {advisor.area_of_focus && advisor.area_of_focus.length > 50 ? advisor.area_of_focus.slice(0, 50) + '…' : advisor.area_of_focus}
                    </td>
                    <td className="py-4 px-4 max-w-xs truncate whitespace-pre-line break-words" title={advisor.bio}>
                      <SafeRichText content={advisor.bio && advisor.bio.length > 50 ? advisor.bio.slice(0, 50) + '…' : advisor.bio} />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-2 items-stretch justify-center">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(advisor)}
                            className="admin-btn-table-edit">
                            Edit
                          </button>
                        )}
                        {canDelete ? (
                          <button
                            onClick={() => handleDeleteAdvisor(advisor.id, advisor.name)}
                            disabled={deleteLoading === advisor.id}
                            className="admin-btn-table-danger">
                            {deleteLoading === advisor.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="admin-btn-table-disabled"
                            title="Superadmin role required to delete advisors">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
